import { USER_LOOKUP_MAX_IDS, type Page, type PublicUser } from '@orbit-sync/shared-types';
import { request } from './client.js';

/**
 * `GET /api/v1/users?q=` - directory search.
 *
 * Matches a display-name fragment, or a full email address. Results never carry
 * an email, so this cannot be used to harvest addresses.
 */
export function searchUsers(
  q: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<PublicUser[]> {
  return request<PublicUser[]>('/users', { query: { q, limit }, signal });
}

/**
 * `GET /api/v1/users/lookup?ids=` - turns ids into names, in one round trip.
 *
 * Unknown ids come back absent rather than as an error, so a deleted account in
 * an old channel does not break the whole batch. Caps the request at the
 * server's own limit so an oversized batch fails here rather than as a 400.
 */
export function lookupUsers(ids: readonly string[], signal?: AbortSignal): Promise<PublicUser[]> {
  const wanted = [...new Set(ids)].slice(0, USER_LOOKUP_MAX_IDS);

  if (wanted.length === 0) {
    return Promise.resolve([]);
  }

  return request<PublicUser[]>('/users/lookup', { query: { ids: wanted.join(',') }, signal });
}

/**
 * `GET /api/v1/users/directory` - everyone, A to Z, a page at a time; `q`
 * narrows it as search does. Pass back `nextCursor` for the next page.
 */
export function browseDirectory(
  query: { q?: string; cursor?: string | null; limit?: number },
  signal?: AbortSignal,
): Promise<Page<PublicUser>> {
  return request<Page<PublicUser>>('/users/directory', {
    query: {
      q: query.q || undefined,
      cursor: query.cursor ?? undefined,
      limit: query.limit,
    },
    signal,
  });
}
